import React, { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Context,
  POST_LESSON_COMPLETED_SUCCESS_MESSAGE,
  createTeachingMaterialViewLog,
  fetchLesson,
  fetchCompletionStatuses,
  fetchLessonRetrospectives,
  postCompletedLesson,
  postLessonRetrospectives,
  fetchOnePagePortfolioAssessment,
  fetchTeachingMaterial,
  fetchOnePagePortfolioAssessmentAnswersVisibility,
  videoLessonPath,
  notFoundPath,
} from "../../../teacherStore";
import { parseQueryParams } from "../../../libs/Util";
import MaterialForImage from "../../../templates/teacher/MaterialForImage";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const history = useHistory();
  const { state: locationState } = useLocation<{
    fromVideoLesson?: boolean;
    lastChapter?: number;
    videoCurrentTime?: number;
  }>();
  const isFromVideoLesson = locationState?.fromVideoLesson ?? false;
  const params = parseQueryParams();
  const teachingMaterialId = parseInt(params["teachingMaterial"]);
  const classListInParams = params["classList"];
  const lessonId = parseInt(params["lesson"]);
  const pageNo = parseInt(params["page"]);
  const roundNo = parseInt(params["round"]) || 1;
  const lastChapter = useRef(locationState?.lastChapter || 1);
  const videoCurrentTime = useRef(locationState?.videoCurrentTime);
  // レンダリングのたびにclassListが異なるメモリに作成され
  // 再度useEffect内の処理が実行してしまうためメモ化
  const classList = useMemo<number[]>(
    () =>
      classListInParams
        ? classListInParams.split(",").map((classID: string) => Number(classID))
        : [],
    [classListInParams],
  );
  const hasVideoChapter = state.lesson?.has_video_chapter ?? false;

  useEffect(() => {
    if (isFromVideoLesson && state.lesson?.id === lessonId) return;
    dispatch(fetchLesson(lessonId, classList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, lessonId]);

  useEffect(() => {
    dispatch(
      createTeachingMaterialViewLog(
        teachingMaterialId,
        lessonId,
        pageNo,
        JSON.stringify(classList),
      ),
    );
  }, [dispatch, lessonId, teachingMaterialId, pageNo, classList]);

  useEffect(() => {
    if (isFromVideoLesson && state.completion_statuses?.lesson_id === lessonId)
      return;
    dispatch(fetchCompletionStatuses(classList, lessonId));
    dispatch(fetchLessonRetrospectives(classList.join(), lessonId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, lessonId, classList, isFromVideoLesson]);

  useEffect(() => {
    if (state.lesson_completed === POST_LESSON_COMPLETED_SUCCESS_MESSAGE) {
      dispatch(fetchCompletionStatuses(classList, lessonId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.lesson_completed, lessonId]);

  useEffect(() => {
    if (isFromVideoLesson && state.teaching_material?.id === teachingMaterialId)
      return;
    dispatch(fetchTeachingMaterial(teachingMaterialId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, teachingMaterialId]);

  const postCompletedLessonFunc = () => {
    dispatch(postCompletedLesson(classList, lessonId));
  };

  const postCompletedLessonRetrospectivesFunc = (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => {
    dispatch(
      postLessonRetrospectives(
        state.teacher.id,
        classList.join(),
        lessonId,
        score,
        lessonDeliveryScore,
        groupWorkScore,
      ),
    );
  };

  const fetchOnePagePortfolioAssessmentFunc = (id: number) => {
    if (isFromVideoLesson && state.one_page_portfolio_assessment?.id === id)
      return;
    dispatch(fetchOnePagePortfolioAssessment(id));
  };

  const handleSwitchToVideo = () => {
    history.push({
      pathname: videoLessonPath,
      search: `?lesson=${lessonId}&chapter=${
        lastChapter.current
      }&classList=${classList.join(",")}`,
      state: {
        fromProjection: true,
        lastProjectionPage: pageNo,
        videoCurrentTime: videoCurrentTime.current,
      },
    });
  };

  const fetchOnePagePortfolioAssessmentAnswersVisibilityFunc = (
    id: number,
    classList: number[],
  ) => {
    if (isFromVideoLesson && state.one_page_portfolio_assessment?.id === id)
      return;
    dispatch(fetchOnePagePortfolioAssessmentAnswersVisibility(id, classList));
  };

  useEffect(() => {
    const assignedClassIds = state.teacher.classes
      .filter(cls => !cls.is_sub)
      .map(cls => cls.id);

    if (assignedClassIds.length === 0) return;
    if (classList.some(cls_id => !assignedClassIds.includes(cls_id))) {
      history.replace(notFoundPath);
    }
  }, [state.teacher.classes, classList, history]);

  return state.lesson && state.lesson.id === lessonId ? (
    <MaterialForImage
      teachingMaterialId={teachingMaterialId}
      lesson={state.lesson}
      classList={classList}
      nextLessonId={state.next_lesson_id}
      prevLessonId={state.prev_lesson_id}
      maxRoundNo={state.max_round_no}
      roundNo={roundNo}
      postCompletedLesson={postCompletedLessonFunc}
      postCompletedLessonRetrospectives={postCompletedLessonRetrospectivesFunc}
      fetchOnePagePortfolioAssessment={fetchOnePagePortfolioAssessmentFunc}
      fetchOnePagePortfolioAssessmentAnswersVisibility={
        fetchOnePagePortfolioAssessmentAnswersVisibilityFunc
      }
      hasVideoChapter={hasVideoChapter}
      onSwitchToVideo={handleSwitchToVideo}
      isFromVideoLesson={isFromVideoLesson}
    />
  ) : null;
};
